import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from 'shared/components/UC/Button';
import styles from './CasCallback.module.css';
import authService, { AuthError } from '../services/authService';

type AuthStatus =
  | 'loading'
  | 'authenticating'
  | 'success'
  | 'error'
  | 'no-ticket';

const CasCallback: React.FC = () => {
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const hasProcessedRef = useRef<boolean>(false);

  useEffect(() => {
    // Prevent multiple executions (React 18 Strict Mode calls useEffect twice)
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const processCallback = async () => {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const ticket = urlParams.get('ticket');

        console.log(
          'CAS Callback - Processing ticket:',
          ticket ? 'present' : 'missing'
        );

        if (!ticket) {
          setStatus('no-ticket');
          return;
        }

        // Start authentication
        setStatus('authenticating');

        const serviceUrl = `${window.location.origin}/cas-callback`;
        const response = await authService.casLogin(ticket, serviceUrl);

        console.log(
          'CAS Authentication successful for user:',
          response.user.email
        );

        // Store authentication data in localStorage
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('auth_user', JSON.stringify(response.user));

        // Update AuthContext immediately
        setAuthData(response.user, response.token);

        setUserEmail(response.user.email);
        setStatus('success');

        // Redirect to role-based dashboard after brief success display
        const getDashboardPath = (role: string) => {
          switch (role) {
            case 'researcher':
              return '/researcher/dashboard';
            case 'evaluator':
              return '/evaluator/dashboard';
            case 'admin':
              return '/admin/dashboard';
            default:
              return '/login';
          }
        };

        setTimeout(() => {
          const dashboardPath = getDashboardPath(response.user.role);
          navigate(dashboardPath, { replace: true });
        }, 2000);
      } catch (error) {
        console.error('CAS Authentication failed:', error);

        if (error instanceof AuthError) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('Unknown authentication error occurred');
        }

        setStatus('error');
      }
    };

    processCallback();
  }, [navigate, setAuthData]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.loadingStatus}>
              <p>🔄 Procesando autenticación de CAS...</p>
            </div>
          </div>
        );

      case 'authenticating':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.authenticatingStatus}>
              <p>🔐 Autenticando con UC CAS...</p>
              <p className={styles.subText}>Validando credenciales...</p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.successStatus}>
              <p>✅ ¡Autenticación exitosa!</p>
              <p className={styles.subText}>Bienvenido, {userEmail}</p>
              <p className={styles.subText}>Redirigiendo al dashboard...</p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.errorStatus}>
              <p>❌ Error de autenticación</p>
              <p className={styles.errorMessage}>{errorMessage}</p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={() => navigate('/login')}>
                  Volver al Login
                </Button>
                <Button
                  variant="primary"
                  onClick={() => window.location.reload()}
                  style={{ marginLeft: '1rem' }}
                >
                  Reintentar
                </Button>
              </div>
            </div>
          </div>
        );

      case 'no-ticket':
        return (
          <div className={styles.statusContainer}>
            <div className={styles.errorStatus}>
              <p>⚠️ Callback inválido</p>
              <p className={styles.errorMessage}>
                No se encontró el ticket de autenticación CAS en la URL.
              </p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={() => navigate('/login')}>
                  Volver al Login
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Autenticación UC CAS</h1>
          <p className={styles.subtitle}>
            Procesando tu autenticación con el sistema UC
          </p>
        </div>

        {renderContent()}

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Sistema de Postulaciones UC - Fondos de Investigación
          </p>
        </div>
      </div>
    </div>
  );
};

export default CasCallback;
